import axios from "axios";
import { resolveTrainDagPath } from "../../utils/modelPaths";
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import NavigationBar from "../../components/NavBar/NavBar";
import DatasetWorkspaceShell from "../../components/Dataset/DatasetWorkspaceShell";
import "../../styles/Dataset.css";
import "../../styles/DatasetPage.css";
import "../../styles/org-buttons.css";
import "./TrainPage.css";
import { Modal, Button, Tabs, Tab, Form } from "react-bootstrap";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import { buttonText } from "../../utils/buttonText";
import { notificationText } from "../../utils/notificationText";
import { pageText } from "../../utils/pageText";
import {
    formatTrainingError,
    normalizeAudioClasses,
    normalizeDetectionClasses,
} from "../../utils/trainingValidation";
import OrganizationTableInput, {
    isOrganizationTableField,
} from "./OrganizationTableInput";
import { DinoClassificationTrain } from "../../components/Classification/ClassificationWorkflow";
import FaceDetectionParameters, {
    parseFaceDetectionConfig,
    faceDetectionConfigFields,
    serializeFaceDetectionConfig,
} from "./FaceDetectionParameters";

const TAG_FIELDS = new Set(["classes", "cls_mapping", "train_table", "val_table", "eval_table", "datafile", "test_datafile", "minio_rec", "minio_test_rec", "minio_train", "minio_val", "minio_data", "minio_zip"]);
const MINIO_ARCHIVE_FIELDS = new Set(["minio_rec", "minio_test_rec", "minio_train", "minio_val", "minio_data", "minio_zip"]);
const YOLO_ADV_MODELS = new Set(["Yolov7_OD", "Yolov7_kpts", "Yolov8s_OD"]);
const TRAIN_CFG_ADV_MODELS = new Set(["Yolov7_OD", "Yolov7_kpts", "Yolov8s_OD", "FR_arcface", "FaceDetection"]);
const MODEL_LABELS = {
    FaceDetection: "Face Detection (VGG7 FCOS)",
    FR_arcface: "FR (ArcFace)",
    Yolov7_OD: "Yolov7 OD",
    Yolov7_kpts: "Yolov7 Kpts",
    Yolov8s_OD: "Yolov8s OD",
    YOLOWorld_OD: "YOLO-World v2 OD",
    AudioResnet: "Audio ResNet",
    Evaluation: "Evaluation",
    dino_classification: "DINO Classification",
};
const DINO_MODEL_LABELS = {dinov2: 'DINOv2 Large', dinov2_base: 'DINOv2 ViT-B/14', dinov3: 'DINOv3 ViT-B/16'};
const DINO_TERMINAL_STATUSES = new Set(['Succeeded', 'Failed', 'Error', 'Skipped', 'Cancelled', 'Canceled', 'Terminated']);

function getModelStatusBadgeClass(status) {
    const normalized = String(status || "pending").toLowerCase();
    return normalized === "succeeded" ? "organizer" : "regular";
}

function modelDownloadKey(model) {
    return model.run_id || resolveTrainDagPath(model) || `${model.solution_name}_${model.platform_name}`;
}

function TrainPage() {
    const userToken = Cookies.get("user_token");
    const userData = userToken ? jwtDecode(userToken) : null;
    const organization = userData ? userData.organization : null;
    const role = userData ? userData.role : null;
    const username = userData ? (userData.username || userData.email) : null;

    const [model, setModel] = useState(null);
    const [models, setModels] = useState([]);
    const [modelInfo, setModelInfo] = useState({ name: "", status: "pending" });
    const [defaultArgs, setDefaultArgs] = useState({});
    const [tagInputs, setTagInputs] = useState({});
    const [organizationTables, setOrganizationTables] = useState([]);
    const [organizationTablesLoading, setOrganizationTablesLoading] = useState(false);

    const [allTrainedModels, setAllTrainedModels] = useState([]);

    // 分组管理状态
    const [checkedModels, setCheckedModels] = useState([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupName, setGroupName] = useState("");

    const [showModelInfoModal, setShowModelInfoModal] = useState(false);
    const [showDinoClassification, setShowDinoClassification] = useState(false);
    const [showFullDetailsModal, setShowFullDetailsModal] = useState(false);
    const [selectedFullDetailsModel, setSelectedFullDetailsModel] = useState({});
    const [showDetailArgsModal, setShowDetailArgsModal] = useState(false);
    const [defaultDetailArgs, setDefaultDetailArgs] = useState({});
    const [advancedParams, setAdvancedParams] = useState({});
    const [defaultTrainCfg, setDefaultTrainCfg] = useState("");
    const [advancedTrainCfg, setAdvancedTrainCfg] = useState("");
    const [faceConfigFields, setFaceConfigFields] = useState({});
    const [faceConfigBaseline, setFaceConfigBaseline] = useState({});
    const [faceConfigTypes, setFaceConfigTypes] = useState({});
    const [faceConfigRequiredFields, setFaceConfigRequiredFields] = useState([]);
    const [defaultDevice, setDefaultDevice] = useState("");
    const [advancedDevice, setAdvancedDevice] = useState("");
    const [defaultBatchSize, setDefaultBatchSize] = useState("");
    const [advancedBatchSize, setAdvancedBatchSize] = useState("");
    const [defaultWorkers, setDefaultWorkers] = useState("");
    const [advancedWorkers, setAdvancedWorkers] = useState("");
    const [showEvalModal, setShowEvalModal] = useState(false);
    const [evalData, setEvalData] = useState(null);
    const [evalLoading, setEvalLoading] = useState(false);
    const [pendingEvalModels, setPendingEvalModels] = useState(new Set());
    const [downloadingModels, setDownloadingModels] = useState(new Set());
    const downloadRequests = useRef(new Set());
    const [pendingModifiedDetailArgs, setPendingModifiedDetailArgs] = useState({});
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorInfo, setErrorInfo] = useState(null);

    const loadTrainedModelsFromServer = async () => {
        try {
            // Admin 不传 organization 参数，后端会返回所有模型
            // 非 admin 传 organization，后端会只返回该组织的模型
            const params = {
                requesterToken: userToken
            };

            // 非 admin 用户才需要指定 organization
            if (role !== 'admin') {
                params.organization = organization || null;
            }

            const response = await axios.get("/kneron/trained-models", { params });
            setAllTrainedModels(response.data || []);
        } catch (error) {
            console.error("Error loading trained models from server:", error);
            const errorMsg = error.response?.data?.error || "Failed to load model cards from server.";
            window.alert(errorMsg);
            setAllTrainedModels([]);
        }
    };

    const saveTrainedModelsToServer = async (models) => {
        try {
            // 修改为新的请求格式：包含 requesterToken 和 models
            await axios.post("/kneron/trained-models", {
                requesterToken: userToken,
                models: models.filter(m => m.model_type !== 'dino_classification')
            });
        } catch (error) {
            console.error("Error saving trained models to server:", error);
            window.alert("Failed to save model cards to server.");
        }
    };

    // 过滤模型：admin 显示所有模型，非 admin 只显示自己组织的模型
    const trainedModels = allTrainedModels.filter(m => {
        // Admin 可以看到所有模型
        if (role === 'admin') {
            return true;
        }

        // 非 admin 只看自己组织的模型
        if (organization) {
            return m.organization === organization;
        } else {
            return !m.organization;  // 显示没有组织的模型
        }
    });

    useEffect(() => {
        loadSupportModelList();
        loadTrainedModelsFromServer();
    }, []);

    /**
     * 将 Succeeded 的 DAG 拷贝到 organization_dags；按 run_id 用 dag_saved_to_org 去重，
     * 避免只在「第一次变成 Succeeded」时保存一次，导致同账号多跑只落盘一份。
     */
    const trySaveDagToOrganization = async (model) => {
        if (!model.run_id || model.status !== 'Succeeded') return model;
        if (model.model_type === 'testing_release' || model.model_type === 'Evaluation' || model.model_type === 'dino_classification') return model;
        if (role === 'admin' || !organization) return model;
        if (model.dag_saved_to_org) return model;

        const dagPath = resolveTrainDagPath(model);
        const dagName = `${model.Model_ID}_${model.createdDate}_${model.model_name}_DAG.json`;

        let next = { ...model, dag_path: dagPath };
        try {
            await axios.post('/kneron/save_dag', {
                requesterToken: userToken,
                dag_path: dagPath,
                model_name: model.model_type || 'unknown',
                dag_name: dagName
            });
            next.dag_saved_to_org = true;
            next.dag_org_saved_at = new Date().toISOString();
            console.log(`✓ DAG saved to organization: ${organization}, run_id=${model.run_id}, model_type=${model.model_type}`);
        } catch (dagError) {
            console.error('Failed to save DAG to organization:', dagError);
            console.error('DAG path:', dagPath);
            // 未标记成功，下次轮询会重试
        }
        return next;
    };

    const fetchUpdatedStatus = async () => {
        try {
            const currentModels = trainedModelsRef.current;
            let shouldReloadFromServer = false;

            const updatedModels = await Promise.all(
                currentModels.map(async (model) => {
                    if (!model.run_id) return model;
                    if (model.status === 'Failed') return model;
                    if (model.model_type === 'dino_classification' && DINO_TERMINAL_STATUSES.has(model.status)) return model;

                    // 已成功但尚未写入组织目录：继续尝试 save_dag（不依赖「刚变成 Succeeded」那一次）
                    if (model.status === 'Succeeded') {
                        return trySaveDagToOrganization(model);
                    }

                    try {
                        const response = model.model_type === 'dino_classification'
                            ? await axios.get(`/classification/runs/${model.run_id}`, {params: {requesterToken: userToken}})
                            : await axios.get(`/kneron/kubeflow/status?run_id=${model.run_id}`);
                        let payload = response.data;
                        if (typeof payload === 'string') {
                            try {
                                payload = JSON.parse(payload);
                            } catch (e) {
                                return model;
                            }
                        }
                        if (payload && payload.error) {
                            shouldReloadFromServer = true;
                            return model;
                        }
                        const st = payload && payload.status;
                        if (!st) {
                            return model;
                        }
                        if (st === 'Terminating' || st === 'Terminated') {
                            shouldReloadFromServer = true;
                        }
                        let updatedModel = { ...model, status: st };

                        if (updatedModel.status === 'Succeeded') {
                            updatedModel = await trySaveDagToOrganization(updatedModel);
                        }
                        return updatedModel;
                    } catch (error) {
                        const code = error.response && error.response.status;
                        const body = error.response && error.response.data;
                        const bodyStr = typeof body === 'string' ? body : (body ? JSON.stringify(body) : '');
                        if (code === 404 || /not found/i.test(bodyStr)) {
                            shouldReloadFromServer = true;
                            return model;
                        }
                        console.warn(
                            `Status poll transient error for ${model.run_id}, keeping status "${model.status}":`,
                            error
                        );
                        return model;
                    }
                })
            );

            if (shouldReloadFromServer) {
                await loadTrainedModelsFromServer();
                return;
            }

            if (JSON.stringify(updatedModels) !== JSON.stringify(currentModels)) {
                setAllTrainedModels(updatedModels);
                await saveTrainedModelsToServer(updatedModels);
            }
        } catch (error) {
            console.error("Global status update er6277745b02e6or:", error);
        }
    };

    // 有运行中的要轮询；另有已成功但未落盘组织目录的也要轮询，直到全部 dag_saved_to_org
    const needsStatusPoll = trainedModels.some(
        (m) => m.run_id && (m.model_type === 'dino_classification'
            ? !DINO_TERMINAL_STATUSES.has(m.status) : m.status !== 'Succeeded' && m.status !== 'Failed')
    );
    const needsOrgSavePoll =
        role !== 'admin' &&
        organization &&
        trainedModels.some(
            (m) =>
                m.run_id &&
                m.status === 'Succeeded' &&
                m.model_type !== 'testing_release' &&
                m.model_type !== 'Evaluation' &&
                m.model_type !== 'dino_classification' &&
                !m.dag_saved_to_org
        );

    const trainedModelsRef = useRef(allTrainedModels);
    useEffect(() => {
        trainedModelsRef.current = allTrainedModels;
    }, [allTrainedModels]);

    useEffect(() => {
        if (!needsStatusPoll && !needsOrgSavePoll) return;
        // 有需要落盘的 Succeeded 时先立刻跑一次，不必等 30s
        fetchUpdatedStatus();
        const interval = setInterval(fetchUpdatedStatus, 30000);
        return () => clearInterval(interval);
    }, [trainedModels, role, organization, needsStatusPoll, needsOrgSavePoll]);

    const loadOrganizationTables = useCallback(async () => {
        if (!userToken) {
            setOrganizationTables([]);
            setOrganizationTablesLoading(false);
            return;
        }
        setOrganizationTablesLoading(true);
        try {
            const response = await axios.post(
                "/datasets/list?names_only=true&media_type=table&scope=organization",
                { requesterToken: userToken }
            );
            const tableNames = [...new Set(
                (Array.isArray(response.data) ? response.data : [])
                    .map((name) => String(name).trim())
                    .filter(Boolean)
            )].sort((left, right) => left.localeCompare(right));
            setOrganizationTables(tableNames);
        } catch (error) {
            console.error("Error loading organization tables:", error);
            setOrganizationTables([]);
        } finally {
            setOrganizationTablesLoading(false);
        }
    }, [userToken]);

    useEffect(() => {
        if (
            showModelInfoModal &&
            Object.keys(defaultArgs).some((key) =>
                isOrganizationTableField(modelInfo.model_type, key)
            )
        ) {
            loadOrganizationTables();
        }
    }, [
        defaultArgs,
        loadOrganizationTables,
        modelInfo.model_type,
        showModelInfoModal,
    ]);

    const loadSupportModelList = async () => {
        try {
            const response = await axios.get("/kneron/supported_models/list");
            setModels(response.data.concat());
        } catch (error) {
            console.error("Error fetching model list:", error);
            window.alert(error.response?.data || "An error occurred");
        }
    };

    const updateModelInfo = async (selectedModel) => {
        try {
            const response = await axios.get(
                `/kneron/supported_models/${selectedModel}`,
                { params: { requesterToken: userToken } }
            );
            const foundModel = response.data;
            setModelInfo({
                model_type: foundModel.model_type,
               ...foundModel.default_args,
            });
            setDefaultArgs(foundModel.default_args);
            setTagInputs({});
            if (selectedModel === "YOLOWorld_OD" || modelInfo.model_type === "YOLOWorld_OD") {
                setPendingModifiedDetailArgs({});
                setDefaultDetailArgs({});
            }
            setShowModelInfoModal(true);
        } catch (error) {
            window.alert(error.response?.data || "An error occurred");
        }
    };

    const handleInputChange = (key, value) => {
        setModelInfo((prevModelInfo) => ({
           ...prevModelInfo,
            [key]: value
        }));
    };

    const toTags = (value) => {
        if (Array.isArray(value)) return value.map(String).map(v => v.trim()).filter(Boolean);
        if (typeof value === "string") return value.split(",").map(v => v.trim()).filter(Boolean);
        return value ? [String(value)] : [];
    };

    // MinIO bucket: lowercase letters, numbers, dots, hyphens only (no underscore).
    const validateMinioArchivePath = (path, field, modelType) => {
        const cleaned = String(path || "").trim().replace(/^\/+/, "");
        if (!cleaned) return `${field} path is empty`;
        const extensions = modelType === "FaceDetection" && field === "minio_train"
            ? [".zip", ".tar", ".tar.gz", ".tgz"]
            : [".zip"];
        if (!extensions.some(extension => cleaned.toLowerCase().endsWith(extension))) {
            return `${field} must be a ${extensions.join(" / ")} path: ${cleaned}`;
        }
        if (!cleaned.includes("/")) {
            return `${field} must be bucket/object.zip, e.g. train-data/dataset.zip (got: ${cleaned})`;
        }
        const bucket = cleaned.split("/")[0];
        if (!/^[a-z0-9][a-z0-9.-]*[a-z0-9]$/.test(bucket) || bucket.includes("_")) {
            return `Invalid MinIO bucket "${bucket}". Use hyphenated names like train-data (not train_data). Path: ${cleaned}`;
        }
        return null;
    };

    const addModelInfoTag = (key) => {
        const tag = (tagInputs[key] || "").trim().replace(/,$/, "");
        if (!tag) return;
        if (MINIO_ARCHIVE_FIELDS.has(key)) {
            const err = validateMinioArchivePath(tag, key, modelInfo.model_type);
            if (err) {
                window.alert(err);
                return;
            }
        }
        const tags = toTags(modelInfo[key]);
        if (!tags.includes(tag)) {
            handleInputChange(key, [...tags, tag].join(","));
        }
        setTagInputs(prev => ({ ...prev, [key]: "" }));
    };

    const removeModelInfoTag = (key, tag) => {
        handleInputChange(key, toTags(modelInfo[key]).filter(t => t !== tag).join(","));
    };

    const handleModelInfoTagKeyDown = (e, key) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addModelInfoTag(key);
        } else if (e.key === "Backspace" && !tagInputs[key]) {
            const tags = toTags(modelInfo[key]);
            handleInputChange(key, tags.slice(0, -1).join(","));
        }
    };

    const handleAdvancedParamChange = (key, value) => {
        setAdvancedParams((prevParams) => ({
            ...prevParams,
            [key]: value
        }));
    };

    const trainModel = async () => {
        if (!modelInfo.model_type) {
            window.alert("Please choose a model.");
            return;
        }
        let submissionModelInfo = { ...modelInfo };
        if (modelInfo.model_type === "FaceDetection") {
            try {
                const classes = normalizeDetectionClasses(modelInfo.classes);
                submissionModelInfo.classes = classes;
                submissionModelInfo.cls_mapping = classes.join(",");
            } catch (error) {
                window.alert(error.message);
                return;
            }
        }
        if (modelInfo.model_type === "YOLOWorld_OD") {
            delete submissionModelInfo.text_class_count;
        }
        if (modelInfo.model_type === "AudioResnet") {
            const { classes, duplicates } = normalizeAudioClasses(modelInfo.cls_mapping);
            if (duplicates.length > 0) {
                window.alert(
                    `Duplicate audio classes after normalization: ${duplicates.join(", ")}`
                );
                return;
            }
            if (classes.length === 0) {
                window.alert("Audio training requires at least one class.");
                return;
            }
            submissionModelInfo.cls_mapping = classes.join(",");
            setModelInfo(submissionModelInfo);
        }
        for (const key of MINIO_ARCHIVE_FIELDS) {
            for (const tag of toTags(submissionModelInfo[key])) {
                const err = validateMinioArchivePath(tag, key, submissionModelInfo.model_type);
                if (err) {
                    window.alert(err);
                    return;
                }
            }
        }
        try {
            let fullDetailArgs = null;
            if (Object.keys(pendingModifiedDetailArgs || {}).length > 0) {
                fullDetailArgs = { ...defaultDetailArgs, ...pendingModifiedDetailArgs };
            }

            const requestData = {
                requesterToken: userToken,
                ...submissionModelInfo,
                ...(fullDetailArgs ? { detail_args: fullDetailArgs } : {})
            };

            // 非 admin 用户才需要指定 organization
            if (role !== 'admin') {
                requestData.organization = organization;
            }

            const response = await axios.post('/kneron/train/start', requestData);
            const { run_id } = response.data;
            const DateTime = new Date();
            const month = String(DateTime.getMonth() + 1).padStart(2, '0');
            const day = String(DateTime.getDate()).padStart(2, '0');
            const year = String(DateTime.getFullYear()).slice(-2);
            const currentDate = `${month}${day}${year}`;

            window.alert(`Started "${modelInfo.model_type}" training pipeline.`);
            setShowModelInfoModal(false);

            const newModel = {
                ...submissionModelInfo,
                ...(Object.keys(pendingModifiedDetailArgs || {}).length > 0
                    ? { detail_args: { ...(modelInfo.detail_args || {}), ...pendingModifiedDetailArgs } }
                    : {}),
                status: "pending",
                run_id: run_id,
                createdDate: currentDate,
                ...(role !== 'admin' ? { organization: organization } : {}),
                created_by: username
            };
            if (newModel.model_type === "YOLOWorld_OD") {
                newModel.dag_path = resolveTrainDagPath(newModel);
            }

            const newModelsList = [...allTrainedModels, newModel];
            setAllTrainedModels(newModelsList);
            await saveTrainedModelsToServer(newModelsList);

            setModel(null);
            setModelInfo({ name: "", status: "pending" });
            setDefaultArgs({});
            setTagInputs({});
        } catch (error) {
            setModelInfo((prevInfo) => ({ ...prevInfo, status: "error" }));
            const responseData = error.response?.data;
            const text = formatTrainingError(
                responseData,
                error.message || "An error occurred"
            );
            window.alert(`Error: ${text}`);
        }
    };

    const closeModelInfoModal = () => {
        setShowModelInfoModal(false);
    };

    const openDetailArgsModal = async () => {
        if (!modelInfo.model_type) return;
        try {
            const detailUrl = `/kneron/supported_models_args/${modelInfo.model_type}`;
            const response = modelInfo.model_type === "YOLOWorld_OD"
                ? await axios.get(detailUrl, { params: { requesterToken: userToken } })
                : await axios.get(detailUrl);
            const foundModel = response.data || {};
            const detailArgs = foundModel.detail_args || {};
            setDefaultDetailArgs(detailArgs);
            setAdvancedParams(modelInfo.model_type === "YOLOWorld_OD"
                ? { ...detailArgs, ...(modelInfo.detail_args || {}), ...pendingModifiedDetailArgs }
                : detailArgs);
            let trainCfg = foundModel.train_cfg || "";
            if (modelInfo.model_type === "FaceDetection") {
                const defaults = parseFaceDetectionConfig(trainCfg);
                const config = { ...defaults, ...parseFaceDetectionConfig(modelInfo.train_cfg || "{}") };
                // The basic form's epochs/batch_size take precedence during submission.
                for (const key of ["epochs", "batch_size"]) {
                    if (modelInfo[key] !== undefined && String(modelInfo[key]).trim() !== "") {
                        config[key] = Number.isFinite(Number(modelInfo[key])) ? Number(modelInfo[key]) : modelInfo[key];
                    }
                }
                trainCfg = JSON.stringify(config, null, 2);
                setFaceConfigBaseline(config);
                setFaceConfigFields(faceDetectionConfigFields(config));
                setFaceConfigTypes(foundModel.train_cfg_types || {});
                setFaceConfigRequiredFields(Object.keys(defaults));
            }
            setDefaultTrainCfg(trainCfg);
            setAdvancedTrainCfg(trainCfg);
            const device = foundModel.device ?? "";
            const batchSize = foundModel.batch_size ?? "";
            const workers = foundModel.workers ?? "";
            setDefaultDevice(String(device));
            setAdvancedDevice(String(device));
            setDefaultBatchSize(String(batchSize));
            setAdvancedBatchSize(String(batchSize));
            setDefaultWorkers(String(workers));
            setAdvancedWorkers(String(workers));
            if (modelInfo.model_type !== "FaceDetection" && modelInfo.model_type !== "YOLOWorld_OD") {
                setPendingModifiedDetailArgs({});
                setModelInfo(prev => ({
                    ...prev,
                    detail_args: detailArgs,
                    train_cfg: trainCfg,
                    device,
                    batch_size: batchSize,
                    workers
                }));
            }
            setShowDetailArgsModal(true);
        } catch (error) {
            console.error("Error fetching detail args:", error);
            window.alert(error.response?.data || "Failed to load detail args");
        }
    };

    const closeDetailArgsModal = () => {
        setShowDetailArgsModal(false);
    };

    const saveAdvancedParameters = async () => {
        try {
            const isFaceDetection = modelInfo.model_type === "FaceDetection";
            const isYoloWorld = modelInfo.model_type === "YOLOWorld_OD";
            const trainCfgToSave = isFaceDetection
                ? serializeFaceDetectionConfig(faceConfigFields, faceConfigBaseline, faceConfigTypes)
                : advancedTrainCfg;
            const modifiedParams = {};
            Object.keys(advancedParams).forEach(key => {
                if (String(advancedParams[key]) !== String(defaultDetailArgs[key] ?? "")) {
                    modifiedParams[key] = advancedParams[key];
                }
            });

            const detailArgsToSave = { ...advancedParams };
            const currentWorldDetails = { ...defaultDetailArgs, ...(modelInfo.detail_args || {}), ...pendingModifiedDetailArgs };
            const worldDetailsChanged = Object.keys(advancedParams).some(key =>
                String(advancedParams[key]) !== String(currentWorldDetails[key] ?? ""));
            const trainCfgChanged = trainCfgToSave !== defaultTrainCfg;
            const deviceChanged = advancedDevice !== defaultDevice;
            const batchSizeChanged = advancedBatchSize !== defaultBatchSize;
            const workersChanged = advancedWorkers !== defaultWorkers;
            if (
                (isYoloWorld ? !worldDetailsChanged : Object.keys(modifiedParams).length === 0) &&
                !trainCfgChanged &&
                !deviceChanged &&
                !batchSizeChanged &&
                !workersChanged
            ) {
                setShowDetailArgsModal(false);
                window.alert("No parameters were modified.");
                return;
            }

            const response = await axios.post(`/kneron/supported_models_args/${modelInfo.model_type}`, {
                detail_args: detailArgsToSave,
                ...(modelInfo.model_type === "FaceDetection" ? {
                    classes: normalizeDetectionClasses(modelInfo.classes)
                } : {}),
                ...(TRAIN_CFG_ADV_MODELS.has(modelInfo.model_type) ? {
                    train_cfg: trainCfgToSave,
                    ...(YOLO_ADV_MODELS.has(modelInfo.model_type) ? {
                        device: advancedDevice,
                        batch_size: advancedBatchSize,
                        workers: advancedWorkers
                    } : {})
                } : {})
            });

            if (response.data.message) {
                const mergedDetailArgs = { ...defaultDetailArgs, ...modifiedParams };
                setModelInfo(prev => ({
                    ...prev,
                    detail_args: isYoloWorld ? detailArgsToSave : modifiedParams,
                    ...(TRAIN_CFG_ADV_MODELS.has(modelInfo.model_type) ? {
                        train_cfg: trainCfgToSave,
                        ...(isFaceDetection ? {
                            epochs: JSON.parse(trainCfgToSave).epochs,
                            batch_size: JSON.parse(trainCfgToSave).batch_size,
                        } : {}),
                        ...(YOLO_ADV_MODELS.has(modelInfo.model_type) ? {
                            device: advancedDevice,
                            batch_size: advancedBatchSize,
                            workers: advancedWorkers
                        } : {})
                    } : {})
                }));
                setDefaultDetailArgs(mergedDetailArgs);
                setDefaultTrainCfg(trainCfgToSave);
                setDefaultDevice(advancedDevice);
                setDefaultBatchSize(advancedBatchSize);
                setDefaultWorkers(advancedWorkers);
                setPendingModifiedDetailArgs(isYoloWorld ? detailArgsToSave : modifiedParams);
                setShowDetailArgsModal(false);
                const modifiedKeys = [
                    ...Object.keys(modifiedParams),
                    ...(trainCfgChanged ? ["train_cfg"] : []),
                    ...(deviceChanged ? ["device"] : []),
                    ...(batchSizeChanged ? ["batch_size"] : []),
                    ...(workersChanged ? ["workers"] : [])
                ];
                window.alert(`Advanced parameters saved successfully! Modified: ${modifiedKeys.join(', ')}`);
            }
        } catch (error) {
            console.error("Error saving advanced parameters:", error);
            const errorMsg = error.response?.data?.error || error.message || "Failed to save advanced parameters";
            window.alert(`Save failed: ${errorMsg}`);
        }
    };

    const openFullDetailsModal = (model) => {
        setSelectedFullDetailsModel(model);
        setShowFullDetailsModal(true);
    };

    const closeFullDetailsModal = () => {
        setShowFullDetailsModal(false);
    };

    const handleDeleteModel = async (modelToDelete) => {
        if (!modelToDelete) return;

        const confirmDelete = window.confirm(`Are you sure you want to delete the model "${modelToDelete.model_name || modelToDelete.solution_name}"?`);

        if (confirmDelete) {
            try {
                const response = await axios.delete('/kneron/trained-models/selected', {
                    data: { requesterToken: userToken, models: [modelToDelete] },
                });
                setCheckedModels((prev) => prev.filter((id) => id !== modelToDelete.run_id));
                await loadTrainedModelsFromServer();
                if (response.data?.failed_count) {
                    throw new Error((response.data.errors || ['Failed to delete model']).join('\n'));
                }
                window.alert(
                    `Successfully deleted ${response.data?.deleted_count ?? 1} model(s)!`
                );
            } catch (error) {
                console.error("Error deleting model:", error);
                const errorMsg = error.response?.data?.error || error.message || "Delete failed";
                window.alert(`Delete failed: ${errorMsg}`);
            }
        }
    };

    // 分组相关函数
    const handleGroupModels = () => {
        if (!checkedModels || checkedModels.length === 0) {
            window.alert("Please select models.");
            return;
        }
        setShowGroupModal(true);
    };

    const moveToGroup = async () => {
        if (!groupName) {
            window.alert("Please input a group name.");
            return;
        }
        try {
            const response = await axios.post('/kneron/trained-models/group', {
                model_ids: checkedModels,
                group_name: groupName
            });
            window.alert(`Models moved to group "${groupName}" successfully!`);
            setShowGroupModal(false);
            setGroupName("");
            setCheckedModels([]);
            await loadTrainedModelsFromServer();
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Failed to move models to group";
            window.alert(errorMsg);
        }
    };

    const removeFromGroup = async () => {
        if (!checkedModels || checkedModels.length === 0) {
            window.alert("Please select models.");
            return;
        }
        try {
            const response = await axios.delete('/kneron/trained-models/group', {
                data: {
                    model_ids: checkedModels
                }
            });
            window.alert("Models removed from group successfully!");
            setShowGroupModal(false);
            setCheckedModels([]);
            await loadTrainedModelsFromServer();
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Failed to remove models from group";
            window.alert(errorMsg);
        }
    };

    const handleDeleteSelected = async () => {
        if (!checkedModels || checkedModels.length === 0) {
            window.alert("Please select models.");
            return;
        }

        const confirmDelete = window.confirm(`Are you sure you want to delete ${checkedModels.length} selected model(s)?`);
        if (!confirmDelete) return;

        try {
            const modelsToDelete = allTrainedModels.filter(m => checkedModels.includes(m.run_id));

            const response = await axios.delete('/kneron/trained-models/selected', {
                data: {
                    requesterToken: userToken,
                    models: modelsToDelete
                }
            });
            if (response.data?.failed_count) {
                await loadTrainedModelsFromServer();
                throw new Error((response.data.errors || ['Failed to delete selected models']).join('\n'));
            }

            window.alert(`Successfully deleted ${response.data.deleted_count} model(s)!`);
            setCheckedModels([]);
            await loadTrainedModelsFromServer();
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || "Failed to delete models";
            window.alert(errorMsg);
        }
    };

    const handleCheckModel = (runId) => {
        setCheckedModels(prev => {
            if (prev.includes(runId)) {
                return prev.filter(id => id !== runId);
            } else {
                return [...prev, runId];
            }
        });
    };



    const handleDownloadModel = async (model) => {
        const isDino = model.model_type === 'dino_classification';
        const dag_path = isDino ? '' : resolveTrainDagPath(model);
        if (!isDino && !dag_path && !(model.model_type === 'nef' && model.solution_name && model.platform_name)) {
            window.alert("Model directory not available on this card.");
            return;
        }
        const downloadKey = modelDownloadKey(model);
        if (downloadRequests.current.has(downloadKey)) return;
        downloadRequests.current.add(downloadKey);
        setDownloadingModels(new Set(downloadRequests.current));
        try {
            const response = await axios.post(isDino ? `/classification/runs/${model.run_id}/artifacts` : '/kneron/downloadDAG', {
                requesterToken: userToken,
                dag_path,
                Model_ID: model.Model_ID,
                createdDate: model.createdDate,
                model_name: model.model_name,
                model_type: model.model_type,
                solution_name: model.solution_name,
                platform_name: model.platform_name,
            }, {
                responseType: 'blob'
            });

            const filename = isDino ? `dino_classification_${model.run_id}.zip` : model.model_type === 'nef'
                ? `${model.solution_name}_${model.platform_name}.zip`
                : `${(dag_path && dag_path.split('/').slice(-2, -1)[0])
                    || `${model.Model_ID}_${model.createdDate}_${model.model_name}`}.zip`;
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download model files:", error);
            let errorMsg = "Failed to download model files";
            try {
                const data = error.response?.data;
                const errorData = data instanceof Blob ? JSON.parse(await data.text()) : data;
                if (errorData?.error) errorMsg = errorData.error;
            } catch (e) { /* keep default */ }
            window.alert(errorMsg);
        } finally {
            downloadRequests.current.delete(downloadKey);
            setDownloadingModels(new Set(downloadRequests.current));
        }
    };

    const handleEvaluation = async () => {
        const matchedModel = trainedModels.find(
            model => model.model_name === modelInfo["model_name"] && model.status === "Succeeded"
        );
        if (!matchedModel) {
            window.alert("No completed model found. Please check if the model_name is correct.");
            return;
        }

        const evalTables = toTags(modelInfo["eval_table"]);
        const minioZips = toTags(modelInfo["minio_zip"]);
        if (!evalTables.length && !minioZips.length) {
            window.alert("Provide an evaluation table or a MinIO ZIP.");
            return;
        }
        for (const zipPath of minioZips) {
            const err = validateMinioArchivePath(zipPath, "minio_zip");
            if (err) {
                window.alert(err);
                return;
            }
        }

        const { model_type, model_name, cls_mapping, nkpt, dag_path } = matchedModel;
        const evaluationId = `eval_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const resolvedCls =
            cls_mapping
            || (model_type === "Liveness" ? ["real", "attack"] : undefined);
        if (!resolvedCls) {
            window.alert("Matched model is missing cls_mapping.");
            return;
        }
        const params = {
            org_type: model_type,
            model_type: modelInfo.model_type,
            requesterToken: userToken,
            eval_table: evalTables,
            minio_zip: minioZips.map((path) => path.replace(/^\/+/, "")).join(","),
            evaluation_id: evaluationId,
            model_name,
            cls_mapping: resolvedCls,
            dag_path
        };
        if (nkpt !== undefined) {
            params.nkpt = nkpt;
        }

        // 非 admin 用户才需要指定 organization
        if (role !== 'admin') {
            params.organization = organization;
        }

        try {
            const response = await axios.post('/kneron/train/start', params);
            window.alert("Evaluation task has been submitted!");
            setShowModelInfoModal(false);

            const DateTime = new Date();
            const month = String(DateTime.getMonth() + 1).padStart(2, '0');
            const day = String(DateTime.getDate()).padStart(2, '0');
            const year = String(DateTime.getFullYear()).slice(-2);
            const currentDate = `${month}${day}${year}`;

            const dagPathArr = dag_path.split('/');
            const dagDir = dagPathArr.slice(0, -1).join('/');

            const newEvalModel = {
                ...params,
                dag_path: dag_path,
                result_path: dagDir + '/' + evaluationId + '_eval_result.json',
                status: "pending",
                run_id: response.data.run_id,
                createdDate: currentDate,
                // Admin 的模型不设置 organization，非 admin 设置
                ...(role !== 'admin' ? { organization: organization } : {}),
                created_by: username
            };

            const newModelsList = [...allTrainedModels, newEvalModel];
            setAllTrainedModels(newModelsList);
            await saveTrainedModelsToServer(newModelsList);
        } catch (error) {
            let errorMsg = error.response?.data || "An error occurred";
            window.alert("Evaluation submission failed: " + errorMsg);
        }
    };

    const handleShowEval = async (trainedModel) => {
        setEvalLoading(true);
        setEvalData(null);
        setShowEvalModal(true);
        try {
            if (trainedModel.model_type === 'dino_classification') {
                const res = await axios.get(`/classification/runs/${trainedModel.run_id}`, {params: {requesterToken: userToken}});
                setEvalData(res.data.metrics || {error: 'Classification metrics are not available yet'});
            } else {
                const res = await axios.post('/kneron/eval/show_result', { path: trainedModel.result_path });
                setEvalData(res.data);
            }
        } catch (e) {
            setEvalData({error: "Failed to read evaluation result"});
        }
        setEvalLoading(false);
    };

    const handleCreateTensorboard = async (trainedModel) => {
        setPendingEvalModels(prev => new Set(prev).add(trainedModel.Model_ID));

        try {
            const res = await axios.post('/kneron/eval/create_tensorboard', {
                model_info: trainedModel
            });

            if (res.data && res.data.tensorboard_run_id) {
                const checkStatus = async () => {
                    try {
                        const statusRes = await axios.get(`/kneron/kubeflow/status?run_id=${res.data.tensorboard_run_id}`);

                        if (statusRes.data && statusRes.data.status === 'Succeeded') {
                            const updatedModel = {
                                ...trainedModel,
                                evalUrl: res.data.url
                            };

                            const updatedModels = allTrainedModels.map(m =>
                                m.Model_ID === trainedModel.Model_ID ? updatedModel : m
                            );
                            setAllTrainedModels(updatedModels);
                            await saveTrainedModelsToServer(updatedModels);

                            setPendingEvalModels(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(trainedModel.Model_ID);
                                return newSet;
                            });

                            clearInterval(interval);
                        } else if (statusRes.data && statusRes.data.status === 'Failed') {
                            setEvalData({error: "TensorBoard creation failed"});
                            setShowEvalModal(true);
                            setPendingEvalModels(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(trainedModel.Model_ID);
                                return newSet;
                            });

                            clearInterval(interval);
                        }
                    } catch (error) {
                        console.error('Status check failed:', error);
                        setEvalData({error: "Failed to check tensorboard status"});
                        setShowEvalModal(true);
                        setPendingEvalModels(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(trainedModel.Model_ID);
                            return newSet;
                        });

                        clearInterval(interval);
                    }
                };

                const interval = setInterval(checkStatus, 5000);

                setTimeout(() => {
                    clearInterval(interval);
                    setPendingEvalModels(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(trainedModel.Model_ID);
                        return newSet;
                    });
                }, 100000);

            } else {
                setEvalData({error: "No tensorboard run ID received from backend"});
                setShowEvalModal(true);
                setPendingEvalModels(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(trainedModel.Model_ID);
                    return newSet;
                });
            }
        } catch (e) {
            console.error('Failed to get tensorboard run ID:', e);
            setEvalData({error: "Failed to get tensorboard run ID"});
            setShowEvalModal(true);
            setPendingEvalModels(prev => {
                const newSet = new Set(prev);
                newSet.delete(trainedModel.Model_ID);
                return newSet;
            });
        }
    };

    // 获取错误信息
    const handleViewError = async (trainedModel) => {
        if (!trainedModel.run_id) {
            window.alert("No run_id available for this model");
            return;
        }

        try {
            const response = await axios.get(`/kneron/kubeflow/error?run_id=${trainedModel.run_id}`);
            setErrorInfo(response.data);
            setShowErrorModal(true);
        } catch (error) {
            console.error('Failed to get error info:', error);
            const errorMsg = error.response?.data?.error || "Failed to fetch error information";
            window.alert(errorMsg);
        }
    };

    // 组织模型数据
    const groupedModels = useMemo(() => {
        const groups = {};
        const ungrouped = [];

        trainedModels.forEach(model => {
            if (model.group) {
                if (!groups[model.group]) {
                    groups[model.group] = [];
                }
                groups[model.group].push(model);
            } else {
                ungrouped.push(model);
            }
        });

        return { groups, ungrouped };
    }, [trainedModels]);

    const modelStats = useMemo(() => ({
        total: trainedModels.length,
        running: trainedModels.filter(m => m.status && !["Succeeded", "Failed"].includes(m.status)).length,
        succeeded: trainedModels.filter(m => m.status === "Succeeded").length,
        failed: trainedModels.filter(m => m.status === "Failed").length,
    }), [trainedModels]);

    // 模型卡片组件
    const ModelCard = ({ trainedModel, index }) => {
        const isChecked = checkedModels.includes(trainedModel.run_id);
        const isDownloading = downloadingModels.has(modelDownloadKey(trainedModel));

        return (
            <div key={index} className="model-card">
                {/* 复选框 */}
                <Form.Check
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCheckModel(trainedModel.run_id)}
                    style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        zIndex: 10
                    }}
                />

                {/* 删除按钮 */}
                <button
                    className="close-btn"
                    onClick={() => handleDeleteModel(trainedModel)}
                >
                    ×
                </button>

                <div className="model-card-header">
                    <div>
                        <span className="model-card-kicker">{trainedModel.model_type === 'dino_classification' ? 'DINO Classification' : trainedModel.model_type || pageText("Model")}</span>
                        <h3 title={trainedModel.model_name || trainedModel.model_type || trainedModel.solution_name}>
                            {trainedModel.model_name || trainedModel.model_type || trainedModel.solution_name}
                        </h3>
                    </div>
                    <span className={`org-role-badge ${getModelStatusBadgeClass(trainedModel.status)}`}>
                        {pageText(trainedModel.status || "pending")}
                    </span>
                </div>

                <div className="model-meta-grid">
                    {trainedModel.model_type === 'dino_classification' && <>
                        <div><span>DINO</span><strong>{DINO_MODEL_LABELS[trainedModel.model] || trainedModel.model}</strong></div>
                        <div><span>Classifier</span><strong>{trainedModel.classifier === 'mlp' ? 'MLP' : 'Logistic Regression'}</strong></div>
                        <div><span>Table</span><strong>{trainedModel.table_name}</strong></div>
                    </>}
                    {trainedModel.Model_ID && <div><span>{pageText("Model ID")}</span><strong>{trainedModel.Model_ID}</strong></div>}
                    {trainedModel.cls_mapping && <div><span>{pageText("Mapping")}</span><strong>{trainedModel.cls_mapping}</strong></div>}
                    {trainedModel.platform_name && <div><span>{pageText("Platform")}</span><strong>{trainedModel.platform_name}</strong></div>}
                    {trainedModel.group && <div><span>{pageText("Group")}</span><strong>{trainedModel.group}</strong></div>}
                </div>

                <div className="model-card-footer">
                {trainedModel.model_type !== 'Evaluation' && trainedModel.status === 'Succeeded' && (
                    <div className="model-card-action-block">
                        <button
                            type="button"
                            className="org-btn org-btn-primary"
                            onClick={() => trainedModel.model_type === 'dino_classification'
                                ? handleShowEval(trainedModel) : handleCreateTensorboard(trainedModel)}
                            disabled={trainedModel.model_type !== 'dino_classification' && pendingEvalModels.has(trainedModel.Model_ID)}
                        >
                            {buttonText(trainedModel.model_type === 'dino_classification' ? 'View Evaluation'
                                : pendingEvalModels.has(trainedModel.Model_ID) ? 'Creating...' : 'Tensorboard')}
                        </button>
                        <button
                            type="button"
                            className="org-btn org-btn-secondary"
                            onClick={() => handleDownloadModel(trainedModel)}
                            disabled={isDownloading}
                        >
                            {buttonText(isDownloading
                                ? "Packaging..." : "Download Model Files")}
                        </button>
                    </div>
                )}

                {(trainedModel.status === 'Failed' || (trainedModel.model_type === 'dino_classification' && trainedModel.status === 'Error')) && trainedModel.run_id && (
                    <button
                        type="button"
                        className="org-btn org-btn-danger"
                        onClick={() => handleViewError(trainedModel)}
                    >
                        {buttonText("View Error")}
                    </button>
                )}

                {trainedModel.model_type === 'Evaluation' && trainedModel.status === 'Succeeded' && (
                    <button
                        type="button"
                        className="org-btn org-btn-primary"
                        onClick={() => handleShowEval(trainedModel)}
                    >
                        {buttonText("View Evaluation")}
                    </button>
                )}

                <div className="model-card-link-row model-card-corner-links">
                    <div className="model-card-corner-left">
                        {trainedModel.evalUrl && (
                            <a
                                href={trainedModel.evalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="model-card-text-link"
                            >
                                Open TensorBoard
                            </a>
                        )}
                    </div>
                    <div className="model-card-corner-right">
                        <button
                            type="button"
                            className="model-card-text-link"
                            onClick={() => openFullDetailsModal(trainedModel)}
                        >
                            {buttonText("Details")}
                        </button>
                    </div>
                </div>
                </div>
            </div>
        );
    };

    // 分组模态框
    const GroupModal = (
        <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)}>
            <Modal.Header closeButton closeLabel={buttonText("Close")}>
                <Modal.Title>Group Management</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Selected {checkedModels.length} model(s)</p>
                <Form.Group>
                    <Form.Label>Group Name:</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter group name"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer className="org-modal-footer">
                <button type="button" className="org-btn org-btn-secondary" onClick={() => setShowGroupModal(false)}>
                    {buttonText("Cancel")}
                </button>
                <button type="button" className="org-btn org-btn-danger" onClick={removeFromGroup}>
                    {buttonText("Remove from Group")}
                </button>
                <button type="button" className="org-btn org-btn-primary" onClick={moveToGroup}>
                    {buttonText("Move to Group")}
                </button>
            </Modal.Footer>
        </Modal>
    );

    return (
        <div>
            <NavigationBar />
            <DatasetWorkspaceShell mainClassName="train-page" showGroups={false}>
            <section className="model-page-header">
                <div>
                    <span className="model-page-eyebrow">{pageText("Kubeflow Training")}</span>
                    <h1>{pageText("Train Models")}</h1>
                    <p>{pageText("Launch training pipelines, monitor run status, and manage generated model artifacts.")}</p>
                </div>
                <div className="model-page-stats">
                    <div><span>{pageText("Total")}</span><strong>{modelStats.total}</strong></div>
                    <div><span>{pageText("Running")}</span><strong>{modelStats.running}</strong></div>
                    <div><span>{pageText("Succeeded")}</span><strong>{modelStats.succeeded}</strong></div>
                    <div><span>{pageText("Failed")}</span><strong>{modelStats.failed}</strong></div>
                </div>
            </section>

            <div className="model-workspace-toolbar">
                <div className="model-toolbar-actions">
                    <button
                        type="button"
                        className="org-btn org-btn-secondary"
                        onClick={handleGroupModels}
                        disabled={checkedModels.length === 0}
                    >
                        {buttonText("Group")}
                    </button>
                    <button
                        type="button"
                        className="org-btn org-btn-danger"
                        onClick={handleDeleteSelected}
                        disabled={checkedModels.length === 0}
                    >
                        {buttonText("Delete Selected")}
                    </button>
                </div>

                <div className="model-toolbar-select">
                    <select
                        value={model || ""}
                        onChange={(e) => {
                            const selectedModel = e.target.value;
                            if (!selectedModel) return;
                            if (selectedModel === "dino_classification") {
                                setShowDinoClassification(true);
                            } else {
                                updateModelInfo(selectedModel);
                            }
                            setModel(null);
                        }}
                        className="local-model-select"
                    >
                        <option value="">{pageText("Please choose a model")}</option>
                        <option value="dino_classification">DINO Classification</option>
                        {models.filter(name => name !== "dino_classification").map((name) => (
                            <option key={name} value={name}>
                                {MODEL_LABELS[name] || name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="model-tabs-panel">
                <Tabs defaultActiveKey="all" className="mb-3">
                    <Tab eventKey="all" title={buttonText("All Models")}>
                        <div className="model-cards-container">
                            {trainedModels.length === 0 ? (
                                <div className="model-empty-state">{pageText("No training runs yet. Choose a model to start a pipeline.")}</div>
                            ) : trainedModels.map((trainedModel, index) => (
                                <ModelCard key={trainedModel.run_id || index} trainedModel={trainedModel} index={index} />
                            ))}
                        </div>
                    </Tab>

                    {Object.keys(groupedModels.groups).map(groupKey => (
                        <Tab
                            key={groupKey}
                            eventKey={groupKey}
                            title={
                                <span>
                                    {groupKey}
                                    <button
                                        className="btn-close model-tab-close"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            const confirmDelete = window.confirm(`Delete group "${groupKey}"? Models will be ungrouped.`);
                                            if (confirmDelete) {
                                                try {
                                                    const groupModelIds = groupedModels.groups[groupKey].map(m => m.run_id);
                                                    await axios.delete('/kneron/trained-models/group', {
                                                        data: { model_ids: groupModelIds }
                                                    });
                                                    await loadTrainedModelsFromServer();
                                                    window.alert(`Group "${groupKey}" deleted successfully!`);
                                                } catch (error) {
                                                    window.alert("Failed to delete group");
                                                }
                                            }
                                        }}
                                    />
                                </span>
                            }
                        >
                            <div className="model-cards-container">
                                {groupedModels.groups[groupKey].map((trainedModel, index) => (
                                    <ModelCard key={trainedModel.run_id || index} trainedModel={trainedModel} index={index} />
                                ))}
                            </div>
                        </Tab>
                    ))}
                </Tabs>
            </div>

            <Modal show={showDinoClassification} onHide={() => setShowDinoClassification(false)} className="wider-modal">
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>DINO Classification</Modal.Title>
                </Modal.Header>
                <Modal.Body>{showDinoClassification && <DinoClassificationTrain embedded onSubmitted={() => {
                    setShowDinoClassification(false);
                    loadTrainedModelsFromServer();
                }} />}</Modal.Body>
            </Modal>

            {/* 模态框 */}
            {GroupModal}

            <Modal
                show={showModelInfoModal}
                onHide={closeModelInfoModal}
                className="wider-modal"
                enforceFocus={!showDetailArgsModal}
            >
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>Model Information: {modelInfo.model_type}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {Object.entries(defaultArgs).filter(([key]) => !(modelInfo.model_type === "YOLOWorld_OD" &&
                        (key === "export_mode" || key === "text_class_count"))).map(([key]) => (
                        <div className="model-info-row" key={key}>
                            <label>{key}:</label>
                            {modelInfo.model_type === "FR_arcface" && key === "cls_mapping" ? (
                                <select
                                    value={modelInfo[key] || "person"}
                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                    className="model-info-input"
                                >
                                    <option value="person">person</option>
                                    <option value="cat">cat</option>
                                </select>
                            ) : isOrganizationTableField(modelInfo.model_type, key) ? (
                                <OrganizationTableInput
                                    value={modelInfo[key]}
                                    options={organizationTables}
                                    onChange={(value) => handleInputChange(key, value)}
                                    loading={organizationTablesLoading}
                                    fieldName={key}
                                />
                            ) : TAG_FIELDS.has(key) ? (
                                <div className="train-tag-input-box">
                                    {toTags(modelInfo[key]).map((tag) => (
                                        <span key={tag} className="train-label-tag">
                                            {tag}
                                            <button
                                                type="button"
                                                className="train-tag-remove"
                                                onClick={() => removeModelInfoTag(key, tag)}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        className="train-tag-text-input"
                                        value={tagInputs[key] || ""}
                                        onChange={(e) => setTagInputs(prev => ({ ...prev, [key]: e.target.value }))}
                                        onKeyDown={(e) => handleModelInfoTagKeyDown(e, key)}
                                        onBlur={() => addModelInfoTag(key)}
                                    />
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    value={modelInfo[key] || ""}
                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                    className="model-info-input"
                                />
                            )}
                        </div>
                    ))}
                    {modelInfo.model_type === "YOLOWorld_OD" && <p>Select local training and validation Tables. Training data is prepared automatically. Weights default to /mnt/models/daat.</p>}
                    {modelInfo.model_type === "YOLOWorld_OD" && (
                        <>
                            <div className="model-info-row">
                                <label htmlFor="world-export-mode">ONNX inputs:</label>
                                <select id="world-export-mode" className="model-info-input"
                                    value={modelInfo.export_mode || "image"}
                                    onChange={e => handleInputChange("export_mode", e.target.value)}>
                                    <option value="image">Image only (fixed vocabulary)</option>
                                    <option value="image_text">Image + text_embeddings</option>
                                </select>
                            </div>
                            <p>{modelInfo.export_mode === "image_text"
                                ? "Static dual inputs. Vocabulary automatically combines COCO 80 classes and additional training classes. N is calculated automatically. For CPU/GPU inference."
                                : "Exports a static image-only model using the training vocabulary."}</p>
                        </>
                    )}
                    <div style={{ textAlign: 'right', marginTop: '8px' }}>
                        <span
                            style={{ color: 'blue', cursor: 'pointer' }}
                            onClick={openDetailArgsModal}
                        >
                            Advanced
                        </span>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModelInfoModal}>{buttonText("Close")}</Button>
                    <Button
                        variant="light"
                        onClick={() => {
                            if (modelInfo.model_type === "Evaluation") {
                                handleEvaluation();
                            } else {
                                trainModel();
                            }
                        }}
                    >
                        {buttonText("Start")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showDetailArgsModal}
                onHide={closeDetailArgsModal}
                className="wider-modal advanced-params-modal"
                backdropClassName="advanced-params-backdrop"
                enforceFocus
                restoreFocus={false}
            >
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>Advanced Parameters</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modelInfo.model_type === "FaceDetection" ? (
                        <FaceDetectionParameters
                            config={faceConfigBaseline}
                            fields={faceConfigFields}
                            types={faceConfigTypes}
                            requiredFields={faceConfigRequiredFields}
                            onChange={(key, value) => setFaceConfigFields(prev => ({ ...prev, [key]: value }))}
                            onRemove={key => setFaceConfigFields(prev => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                            })}
                        />
                    ) : Object.entries(defaultDetailArgs).length === 0 ? (
                        <p>{pageText("No advanced parameters.")}</p>
                    ) : (
                        Object.entries(defaultDetailArgs).map(([key]) => (
                            <div className="model-info-row" key={key}>
                                <label>{modelInfo.model_type === "YOLOWorld_OD" && key === "batch_size" ? "batch-size" : key}:</label>
                                <input
                                    type="text"
                                    value={advancedParams[key] ?? ""}
                                    onChange={(e) => handleAdvancedParamChange(key, e.target.value)}
                                    className="model-info-input"
                                />
                            </div>
                        ))
                    )}
                    {YOLO_ADV_MODELS.has(modelInfo.model_type) && (
                        <>
                            <div className="model-info-row">
                                <label>batch-size:</label>
                                <input
                                    type="text"
                                    value={advancedBatchSize}
                                    onChange={(e) => setAdvancedBatchSize(e.target.value)}
                                    className="model-info-input"
                                />
                            </div>
                            <div className="model-info-row">
                                <label>device:</label>
                                <input
                                    type="text"
                                    value={advancedDevice}
                                    onChange={(e) => setAdvancedDevice(e.target.value)}
                                    className="model-info-input"
                                />
                            </div>
                            <div className="model-info-row">
                                <label>workers:</label>
                                <input
                                    type="text"
                                    value={advancedWorkers}
                                    onChange={(e) => setAdvancedWorkers(e.target.value)}
                                    className="model-info-input"
                                />
                            </div>
                        </>
                    )}
                    {TRAIN_CFG_ADV_MODELS.has(modelInfo.model_type) && modelInfo.model_type !== "FaceDetection" && (
                        <div className="model-info-row">
                            <label>train_cfg.yaml:</label>
                            <Form.Control
                                as="textarea"
                                rows={18}
                                value={advancedTrainCfg}
                                onChange={(e) => setAdvancedTrainCfg(e.target.value)}
                                className="model-info-input"
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDetailArgsModal}>{buttonText("Close")}</Button>
                    <Button variant="primary" onClick={saveAdvancedParameters}>{buttonText("Save Parameters")}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showFullDetailsModal} onHide={closeFullDetailsModal} className="wider-modal">
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>Full Model Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {Object.entries(selectedFullDetailsModel).map(([key, value]) => (
                        <p key={key}>{`${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`}</p>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeFullDetailsModal}>{buttonText("Close")}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} size="xl">
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>
                        {notificationText("Pipeline Error Details")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {errorInfo ? (
                        <div>
                            <p><strong>{buttonText("run id:")}</strong> {errorInfo.run_id}</p>
                            <p><strong>{buttonText("Status")}:</strong> <span style={{color: 'red'}}>{notificationText(errorInfo.status)}</span></p>

                            {errorInfo.error_code && (
                                <p><strong>{notificationText("Error Code:")}</strong> {errorInfo.error_code}</p>
                            )}
                            {errorInfo.failed_stage && (
                                <p><strong>{notificationText("Failed Stage:")}</strong> {notificationText(errorInfo.failed_stage)}</p>
                            )}
                            {errorInfo.root_cause && (
                                <div style={{ marginTop: '16px' }}>
                                    <strong>{notificationText("Root Cause:")}</strong>
                                    <pre style={{
                                        backgroundColor: '#fff3f3',
                                        padding: '12px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontSize: '0.9em'
                                    }}>
                                        {notificationText(errorInfo.root_cause)}
                                    </pre>
                                </div>
                            )}

                            {errorInfo.error_message && (
                                <div style={{ marginTop: '16px' }}>
                                    <strong>{notificationText("Error Message:")}</strong>
                                    <pre style={{
                                        backgroundColor: '#f8f9fa',
                                        padding: '12px',
                                        borderRadius: '4px',
                                        border: '1px solid #dee2e6',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontSize: '0.9em'
                                    }}>
                                        {notificationText(errorInfo.error_message)}
                                    </pre>
                                </div>
                            )}

                            {errorInfo.failed_components && errorInfo.failed_components.length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    <strong>{notificationText("Failed Components:")}</strong>
                                    {errorInfo.failed_components.map((comp, idx) => (
                                        <div key={idx} style={{
                                            marginTop: '12px',
                                            padding: '12px',
                                            backgroundColor: '#fff3cd',
                                            border: '1px solid #ffc107',
                                            borderRadius: '4px'
                                        }}>
                                            <p style={{ margin: '0 0 8px 0' }}>
                                                <strong>{notificationText("Component:")}</strong> {comp.component_name}
                                            </p>
                                            <p style={{ margin: '0 0 8px 0', fontSize: '0.9em' }}>
                                                <strong>{notificationText("Message:")}</strong> {notificationText(comp.message)}
                                            </p>
                                            {comp.error_code && (
                                                <p style={{ margin: '0 0 8px 0', fontSize: '0.9em' }}>
                                                    <strong>{notificationText("Error Code:")}</strong> {comp.error_code}
                                                </p>
                                            )}
                                            {comp.failed_stage && (
                                                <p style={{ margin: '0 0 8px 0', fontSize: '0.9em' }}>
                                                    <strong>{notificationText("Failed Stage:")}</strong> {notificationText(comp.failed_stage)}
                                                </p>
                                            )}
                                            {comp.root_cause && (
                                                <p style={{ margin: '0 0 8px 0', fontSize: '0.9em' }}>
                                                    <strong>{notificationText("Root Cause:")}</strong> {notificationText(comp.root_cause)}
                                                </p>
                                            )}

                                            {comp.logs && (
                                                <div style={{ marginTop: '12px' }}>
                                                    <strong>{notificationText("Container Logs:")}</strong>
                                                    <pre style={{
                                                        backgroundColor: '#1e1e1e',
                                                        color: '#d4d4d4',
                                                        padding: '12px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #333',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontSize: '0.85em',
                                                        maxHeight: '400px',
                                                        overflowY: 'auto',
                                                        marginTop: '8px',
                                                        fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                                                    }}>
                                                        {comp.logs}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!errorInfo.error_message && (!errorInfo.failed_components || errorInfo.failed_components.length === 0) && (
                                <p>
                                    {notificationText(
                                        "No detailed error information available. Please check Kubeflow UI."
                                    )}
                                </p>
                            )}
                        </div>
                    ) : (
                        <p>{notificationText("Loading error information...")}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>{buttonText("Close")}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEvalModal} onHide={() => setShowEvalModal(false)} size="lg">
                <Modal.Header closeButton closeLabel={buttonText("Close")}>
                    <Modal.Title>{notificationText("Evaluation Results")}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    {evalLoading ? (
                        <p>{pageText("Loading...")}</p>
                    ) : evalData ? (
                        evalData.error ? (
                            <p style={{color: 'red'}}>{notificationText(evalData.error)}</p>
                        ) : evalData.success ? (
                            <div>
                                <p style={{color: 'green'}}>{notificationText(evalData.success)}</p>
                                <p>
                                    {notificationText(
                                        "You can now click the evaluation link in the model card to view the results."
                                    )}
                                </p>
                            </div>
                        ) : (
                            <pre style={{whiteSpace: "pre-wrap"}}>{typeof evalData === "string" ? evalData : JSON.stringify(evalData, null, 2)}</pre>
                        )
                    ) : (
                        <p>{notificationText("No Data")}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEvalModal(false)}>
                        {buttonText("Close")}
                    </Button>
                </Modal.Footer>
            </Modal>
            </DatasetWorkspaceShell>
        </div>
    );
}

export default TrainPage;
